import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuLabel,
  ContextMenuSeparator,
} from '@/components/ui/context-menu'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ListTodo,
  Search,
  X,
  LayoutPanelLeft,
  MoreVertical,
  Copy,
  Trash2,
  Pin,
  Plus,
  Eye,
  EyeOff,
  Smile,
  Settings2,
  GripVertical,
} from 'lucide-react'
import { ModeToggle } from '@/components/mode-toggle'
import { ListEmojiPicker } from './ui/list-emoji-picker'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { SelectionArea } from '@viselect/react'
import { cn } from '@/lib/utils'
import { haptics } from '@/lib/haptics'
import { actions, store } from '@/store'
import { HeaderDisplay } from './header'
import { VirtualList } from './ui/virtual-list'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Checkbox } from '@/components/ui/checkbox'
import { useIsTouchCapable } from '@/hooks/use-touch-capable'
import { Skeleton } from '@/components/ui/skeleton'

const SidebarSkeleton = () => (
  <div className="flex flex-col h-full p-3 space-y-6">
    <div className="flex items-center justify-between">
      <Skeleton className="h-8 w-8 rounded-md" />
      <Skeleton className="h-8 w-32 rounded-md" />
      <Skeleton className="h-8 w-8 rounded-full" />
    </div>
    <Skeleton className="h-8 w-full rounded-md" />
    <div className="space-y-3">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center space-x-3">
          <Skeleton className="h-7 w-7 rounded-lg" />
          <Skeleton className="h-4 flex-1 rounded-md" />
          <Skeleton className="h-4 w-6 rounded-full" />
        </div>
      ))}
    </div>
  </div>
)

const SIDEBAR_LIST_ROW_SHELL =
  'w-full rounded-xl px-2.5 py-2 text-[13px] font-medium transition-all duration-200'
const SIDEBAR_LIST_ROW_INNER = 'flex min-w-0 items-center gap-2.5'
const SIDEBAR_LIST_ICON_SLOT =
  'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-border/20 bg-background/80 shadow-sm'

const getSidebarWidth = (isMobile, showSidebar, width) => {
  if (isMobile) return 'min(85vw, 300px)'
  return showSidebar ? `${width}px` : '0px'
}
const getSidebarX = (isMobile, showSidebar) => {
  if (!isMobile) return '0'
  return showSidebar ? '0' : '-100%'
}
const getSidebarOpacity = (isMobile, showSidebar) => (showSidebar || isMobile ? 1 : 0)
const shouldCloseSidebar = (info) => info.offset.x < -50 || info.velocity.x < -300
const shouldOpenSidebar = (info) => info.offset.x > 50 || info.velocity.x > 300
export function TaskSidebar({
  snap,
  isMobile,
  isResizing,
  handleSidebarResizeMouseDown,
  handleSidebarResizeKeyDown,
  handleToggleSidebar,
  handleBackdropKeyDown,
  listSearchQuery,
  handleListSearchChange,
  handleClearListSearch,
  handleSelectAllLists,
  selectedListId,
  todosLength,
  filteredLists,
  todos,
  renamingListId,
  listEmojiHandlers,
  noopListUpdate,
  renameValue,
  handleRenameValueChange,
  handleRenameKeyDown,
  handleRenameBlur,
  handleRenameSaveClick,
  handleListItemClick,
  handleListItemKeyDown,
  selectedListIds,
  handleListRenameClick,
  handleListDuplicateClick,
  handleListPinClick,
  handleListDeleteClick,
  newListEmoji,
  newListColor,
  handleNewListEmojiSelect,
  newListName,
  handleNewListNameChange,
  handleNewListKeyDown,
  handleAddList,
  handleListDragCondition,
  handleListSelectoStart,
  handleListSelecto,
  handleListSelectoEnd,
  handleToggleVisibility,
  weatherUnit,
  timeFormat,
  handleToggleWeatherUnit,
  handleToggleTimeFormat,
  setIsDraggingList,
}) {
  const isTouchCapable = useIsTouchCapable()
  const handleSearchKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        handleClearListSearch()
      }
    },
    [handleClearListSearch],
  )
  return (
    <div className="contents">
      <SidebarToggleFixed
        showSidebar={snap.uiSettings.showSidebar}
        handleToggleSidebar={handleToggleSidebar}
        isMobile={isMobile}
      />
      <SidebarBackdrop
        isMobile={isMobile}
        showSidebar={snap.uiSettings.showSidebar}
        handleToggleSidebar={handleToggleSidebar}
        handleBackdropKeyDown={handleBackdropKeyDown}
      />
      <SidebarContainer
        snap={snap}
        isMobile={isMobile}
        isResizing={isResizing}
        handleToggleSidebar={handleToggleSidebar}
        handleToggleVisibility={handleToggleVisibility}
        listSearchQuery={listSearchQuery}
        handleListSearchChange={handleListSearchChange}
        handleSearchKeyDown={handleSearchKeyDown}
        handleClearListSearch={handleClearListSearch}
        handleSelectAllLists={handleSelectAllLists}
        selectedListId={selectedListId}
        todosLength={todosLength}
        filteredLists={filteredLists}
        todos={todos}
        renamingListId={renamingListId}
        listEmojiHandlers={listEmojiHandlers}
        noopListUpdate={noopListUpdate}
        renameValue={renameValue}
        handleRenameValueChange={handleRenameValueChange}
        handleRenameKeyDown={handleRenameKeyDown}
        handleRenameBlur={handleRenameBlur}
        handleRenameSaveClick={handleRenameSaveClick}
        handleListItemClick={handleListItemClick}
        handleListItemKeyDown={handleListItemKeyDown}
        selectedListIds={selectedListIds}
        handleListRenameClick={handleListRenameClick}
        handleListDuplicateClick={handleListDuplicateClick}
        handleListPinClick={handleListPinClick}
        handleListDeleteClick={handleListDeleteClick}
        newListEmoji={newListEmoji}
        newListColor={newListColor}
        handleNewListEmojiSelect={handleNewListEmojiSelect}
        newListName={newListName}
        handleNewListNameChange={handleNewListNameChange}
        handleNewListKeyDown={handleNewListKeyDown}
        handleAddList={handleAddList}
        handleSidebarResizeMouseDown={handleSidebarResizeMouseDown}
        handleSidebarResizeKeyDown={handleSidebarResizeKeyDown}
        handleListDragCondition={handleListDragCondition}
        handleListSelectoStart={handleListSelectoStart}
        handleListSelecto={handleListSelecto}
        handleListSelectoEnd={handleListSelectoEnd}
        weatherUnit={weatherUnit}
        timeFormat={timeFormat}
        handleToggleWeatherUnit={handleToggleWeatherUnit}
        handleToggleTimeFormat={handleToggleTimeFormat}
        isTouchCapable={isTouchCapable}
        setIsDraggingList={setIsDraggingList}
      />
    </div>
  )
}
const SidebarToggleFixed = ({ showSidebar, handleToggleSidebar, isMobile }) => (
  <Button
    variant="outline"
    size="icon"
    onClick={handleToggleSidebar}
    className={cn(
      'fixed top-3 left-3 z-[100] shadow-sm group transition-opacity duration-300',
      showSidebar && !isMobile && 'opacity-0 pointer-events-none',
    )}
  >
    <LayoutPanelLeft
      className={cn(
        'h-[1.2rem] w-[1.2rem] text-muted-foreground transition-all duration-300',
        'group-hover:text-primary',
        !showSidebar && 'rotate-180',
      )}
    />
  </Button>
)
const SidebarBackdrop = ({ isMobile, showSidebar, handleToggleSidebar, handleBackdropKeyDown }) => (
  <AnimatePresence>
    {isMobile && showSidebar && (
      <motion.div
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 1,
        }}
        exit={{
          opacity: 0,
        }}
        role="button"
        tabIndex={0}
        onClick={handleToggleSidebar}
        onKeyDown={handleBackdropKeyDown}
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[65]"
      />
    )}
  </AnimatePresence>
)
const SidebarContainer = ({
  snap,
  isMobile,
  isResizing,
  handleToggleSidebar,
  handleToggleVisibility,
  listSearchQuery,
  handleListSearchChange,
  handleSearchKeyDown,
  handleClearListSearch,
  handleSelectAllLists,
  selectedListId,
  todosLength,
  filteredLists,
  todos,
  renamingListId,
  listEmojiHandlers,
  noopListUpdate,
  renameValue,
  handleRenameValueChange,
  handleRenameKeyDown,
  handleRenameBlur,
  handleRenameSaveClick,
  handleListItemClick,
  handleListItemKeyDown,
  selectedListIds,
  handleListRenameClick,
  handleListDuplicateClick,
  handleListPinClick,
  handleListDeleteClick,
  newListEmoji,
  newListColor,
  handleNewListEmojiSelect,
  newListName,
  handleNewListNameChange,
  handleNewListKeyDown,
  handleAddList,
  handleSidebarResizeMouseDown,
  handleSidebarResizeKeyDown,
  handleListDragCondition,
  handleListSelectoStart,
  handleListSelecto,
  handleListSelectoEnd,
  weatherUnit,
  timeFormat,
  handleToggleWeatherUnit,
  handleToggleTimeFormat,
  isTouchCapable,
  setIsDraggingList,
}) => {
  const sidebarAnimation = useMemo(
    () => ({
      width: getSidebarWidth(isMobile, snap.uiSettings.showSidebar, snap.uiSettings.sidebarWidth),
      x: getSidebarX(isMobile, snap.uiSettings.showSidebar),
      opacity: getSidebarOpacity(isMobile, snap.uiSettings.showSidebar),
    }),
    [isMobile, snap.uiSettings.showSidebar, snap.uiSettings.sidebarWidth],
  )
  const handleSidebarDragEnd = useCallback(
    (_e, info) => {
      if (shouldCloseSidebar(info) && snap.uiSettings.showSidebar) {
        haptics.impact()
        handleToggleSidebar()
        return
      }
      if (shouldOpenSidebar(info) && !snap.uiSettings.showSidebar) {
        haptics.impact()
        handleToggleSidebar()
      }
    },
    [handleToggleSidebar, snap.uiSettings.showSidebar],
  )
  const handleSidebarPeekClick = useCallback(() => {
    if (!snap.uiSettings.showSidebar) {
      haptics.impact()
      handleToggleSidebar()
    }
  }, [handleToggleSidebar, snap.uiSettings.showSidebar])
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <motion.div
          initial={false}
          animate={sidebarAnimation}
          transition={
            isResizing
              ? {
                  duration: 0,
                }
              : {
                  type: 'spring',
                  stiffness: 350,
                  damping: 35,
                }
          }
          drag={isMobile ? 'x' : false}
          dragConstraints={{
            left: isMobile ? -300 : -100,
            right: 0,
          }}
          dragElastic={isMobile ? 0.05 : 0.1}
          onDragEnd={handleSidebarDragEnd}
          className={cn(
            'flex flex-col border-none overflow-visible shrink-0',
            isMobile
              ? 'fixed inset-y-0 left-0 z-[70] bg-background shadow-2xl'
              : 'relative bg-muted/30 max-w-[34vw]',
            !snap.uiSettings.showSidebar && !isMobile && 'pointer-events-none',
          )}
          id="sidebar-container"
        >
          {isMobile && (
            <button
              type="button"
              className="absolute top-1/2 -translate-y-1/2 -right-6 h-24 w-8 bg-transparent cursor-pointer z-[100]"
              onClick={handleSidebarPeekClick}
              aria-label="Peek sidebar"
            />
          )}
          <SidebarInnerContent
            listSearchQuery={listSearchQuery}
            handleListSearchChange={handleListSearchChange}
            handleSearchKeyDown={handleSearchKeyDown}
            handleClearListSearch={handleClearListSearch}
            handleSelectAllLists={handleSelectAllLists}
            selectedListId={selectedListId}
            todosLength={todosLength}
            filteredLists={filteredLists}
            todos={todos}
            renamingListId={renamingListId}
            listEmojiHandlers={listEmojiHandlers}
            noopListUpdate={noopListUpdate}
            renameValue={renameValue}
            handleRenameValueChange={handleRenameValueChange}
            handleRenameKeyDown={handleRenameKeyDown}
            handleRenameBlur={handleRenameBlur}
            handleRenameSaveClick={handleRenameSaveClick}
            handleListItemClick={handleListItemClick}
            handleListItemKeyDown={handleListItemKeyDown}
            selectedListIds={selectedListIds}
            handleListRenameClick={handleListRenameClick}
            handleListDuplicateClick={handleListDuplicateClick}
            handleListPinClick={handleListPinClick}
            handleListDeleteClick={handleListDeleteClick}
            newListEmoji={newListEmoji}
            newListColor={newListColor}
            handleNewListEmojiSelect={handleNewListEmojiSelect}
            newListName={newListName}
            handleNewListNameChange={handleNewListNameChange}
            handleNewListKeyDown={handleNewListKeyDown}
            handleAddList={handleAddList}
            isMobile={isMobile}
            snap={snap}
            handleSidebarResizeMouseDown={handleSidebarResizeMouseDown}
            handleSidebarResizeKeyDown={handleSidebarResizeKeyDown}
            handleListDragCondition={handleListDragCondition}
            handleListSelectoStart={handleListSelectoStart}
            handleListSelecto={handleListSelecto}
            handleListSelectoEnd={handleListSelectoEnd}
            weatherUnit={weatherUnit}
            timeFormat={timeFormat}
            handleToggleWeatherUnit={handleToggleWeatherUnit}
            handleToggleTimeFormat={handleToggleTimeFormat}
            handleToggleSidebar={handleToggleSidebar}
            isTouchCapable={isTouchCapable}
            setIsDraggingList={setIsDraggingList}
          />
        </motion.div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-44">
        <ContextMenuItem data-visibility-key="showSidebar" onClick={handleToggleVisibility}>
          {snap.uiSettings.showSidebar ? (
            <EyeOff className="mr-2 h-3.5 w-3.5" />
          ) : (
            <Eye className="mr-2 h-3.5 w-3.5" />
          )}
          {snap.uiSettings.showSidebar ? 'Hide Menu' : 'Show Menu'}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  )
}
function SidebarInnerContent({
  listSearchQuery,
  handleListSearchChange,
  handleSearchKeyDown,
  handleClearListSearch,
  handleSelectAllLists,
  selectedListId,
  todosLength,
  filteredLists,
  todos,
  renamingListId,
  listEmojiHandlers,
  noopListUpdate,
  renameValue,
  handleRenameValueChange,
  handleRenameKeyDown,
  handleRenameBlur,
  handleRenameSaveClick,
  handleListItemClick,
  handleListItemKeyDown,
  selectedListIds,
  handleListRenameClick,
  handleListDuplicateClick,
  handleListPinClick,
  handleListDeleteClick,
  newListEmoji,
  newListColor,
  handleNewListEmojiSelect,
  newListName,
  handleNewListNameChange,
  handleNewListKeyDown,
  handleAddList,
  isMobile,
  snap,
  handleSidebarResizeMouseDown,
  handleSidebarResizeKeyDown,
  handleListDragCondition,
  handleListSelectoStart,
  handleListSelecto,
  handleListSelectoEnd,
  weatherUnit,
  timeFormat,
  handleToggleWeatherUnit,
  handleToggleTimeFormat,
  handleToggleSidebar,
  isTouchCapable,
  setIsDraggingList,
}) {
  if (snap.isLoading) {
    return <SidebarSkeleton />
  }

  return (
    <div className="flex flex-col h-full">
      <SidebarHeaderSection
        todos={todos}
        weatherUnit={weatherUnit}
        timeFormat={timeFormat}
        snap={snap}
        handleToggleWeatherUnit={handleToggleWeatherUnit}
        handleToggleTimeFormat={handleToggleTimeFormat}
        handleToggleSidebar={handleToggleSidebar}
      />
      <SidebarSearchSection
        listSearchQuery={listSearchQuery}
        handleListSearchChange={handleListSearchChange}
        handleSearchKeyDown={handleSearchKeyDown}
        handleClearListSearch={handleClearListSearch}
      />
      <SidebarListSection
        handleSelectAllLists={handleSelectAllLists}
        selectedListId={selectedListId}
        todosLength={todosLength}
        filteredLists={filteredLists}
        todos={todos}
        renamingListId={renamingListId}
        listEmojiHandlers={listEmojiHandlers}
        noopListUpdate={noopListUpdate}
        renameValue={renameValue}
        handleRenameValueChange={handleRenameValueChange}
        handleRenameKeyDown={handleRenameKeyDown}
        handleRenameBlur={handleRenameBlur}
        handleRenameSaveClick={handleRenameSaveClick}
        handleListItemClick={handleListItemClick}
        handleListItemKeyDown={handleListItemKeyDown}
        selectedListIds={selectedListIds}
        handleListRenameClick={handleListRenameClick}
        handleListDuplicateClick={handleListDuplicateClick}
        handleListPinClick={handleListPinClick}
        handleListDeleteClick={handleListDeleteClick}
        newListEmoji={newListEmoji}
        newListColor={newListColor}
        handleNewListEmojiSelect={handleNewListEmojiSelect}
        newListName={newListName}
        handleNewListNameChange={handleNewListNameChange}
        handleNewListKeyDown={handleNewListKeyDown}
        handleAddList={handleAddList}
        isTouchCapable={isTouchCapable}
        setIsDraggingList={setIsDraggingList}
      />
      <SidebarFooterSection
        weatherUnit={weatherUnit}
        timeFormat={timeFormat}
        handleToggleWeatherUnit={handleToggleWeatherUnit}
        handleToggleTimeFormat={handleToggleTimeFormat}
        snap={snap}
        todos={todos}
      />
      <SidebarResizeHandle
        isMobile={isMobile}
        showSidebar={snap.uiSettings.showSidebar}
        handleSidebarResizeMouseDown={handleSidebarResizeMouseDown}
        handleSidebarResizeKeyDown={handleSidebarResizeKeyDown}
      />
      {!isTouchCapable && (
        <SidebarSelectoLayer
          handleListDragCondition={handleListDragCondition}
          handleListSelectoStart={handleListSelectoStart}
          handleListSelecto={handleListSelecto}
          handleListSelectoEnd={handleListSelectoEnd}
        />
      )}
    </div>
  )
}
export function SidebarHeaderSection({
  todos,
  weatherUnit,
  timeFormat,
  snap,
  handleToggleWeatherUnit,
  handleToggleTimeFormat,
  handleToggleSidebar,
}) {
  const handleToggleTimeVisibility = useCallback(() => {
    actions.toggleUIVisibility('showTime')
  }, [])
  const handleToggleWeatherVisibility = useCallback(() => {
    actions.toggleUIVisibility('showWeather')
  }, [])
  const handleToggleStatsVisibility = useCallback(() => {
    actions.toggleUIVisibility('showStats')
  }, [])

  const handleToggleThemeVisibility = useCallback(() => {
    actions.toggleUIVisibility('showTheme')
  }, [])

  return (
    <div className="px-3 py-3 flex items-center justify-between gap-1.5 min-w-0">
      <Button
        variant="ghost"
        size="icon"
        onClick={handleToggleSidebar}
        className="h-8 w-8 shrink-0 hover:bg-muted/80 transition-all active:scale-95"
      >
        <LayoutPanelLeft className="h-4 w-4 text-muted-foreground" />
      </Button>
      <div className="flex-1 min-w-0 flex justify-center items-center opacity-70 hover:opacity-100 transition-opacity scale-90 overflow-hidden">
        <HeaderDisplay
          todos={todos}
          weatherUnit={weatherUnit}
          timeFormat={timeFormat}
          showTime={snap.uiSettings.showTime}
          showWeather={snap.uiSettings.showWeather}
          showStats={snap.uiSettings.showStats}
          onToggleTimeVisibility={handleToggleTimeVisibility}
          onToggleWeatherVisibility={handleToggleWeatherVisibility}
          onToggleStatsVisibility={handleToggleStatsVisibility}
          onToggleTimeFormat={handleToggleTimeFormat}
          onToggleWeatherUnit={handleToggleWeatherUnit}
        />
      </div>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div className="flex items-center justify-center min-w-[32px] min-h-[32px]">
            <ModeToggle />
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem onClick={handleToggleThemeVisibility}>
            {snap.uiSettings.showTheme ? (
              <>
                <EyeOff className="mr-2 h-3.5 w-3.5" /> Hide Theme
              </>
            ) : (
              <>
                <Eye className="mr-2 h-3.5 w-3.5" /> Show Theme
              </>
            )}
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    </div>
  )
}
const SidebarSearchSection = ({
  listSearchQuery,
  handleListSearchChange,
  handleSearchKeyDown,
  handleClearListSearch,
}) => (
  <div className="px-3 mb-2">
    <div className="relative group/search">
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground group-focus-within/search:text-primary transition-colors" />
      <input
        type="text"
        value={listSearchQuery}
        onChange={handleListSearchChange}
        onKeyDown={handleSearchKeyDown}
        placeholder="Search lists..."
        className="w-full h-8 bg-muted/50 border border-transparent rounded-md pl-8 pr-7 text-xs focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all placeholder:text-muted-foreground/50 shadow-sm"
      />
      {listSearchQuery && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={handleClearListSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded-full transition-colors"
            >
              <X className="h-3 w-3 text-muted-foreground" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Clear search</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  </div>
)
const SidebarListSection = ({
  handleSelectAllLists,
  selectedListId,
  todosLength,
  filteredLists,
  todos,
  renamingListId,
  listEmojiHandlers,
  noopListUpdate,
  renameValue,
  handleRenameValueChange,
  handleRenameKeyDown,
  handleRenameBlur,
  handleRenameSaveClick,
  handleListItemClick,
  handleListItemKeyDown,
  selectedListIds,
  handleListRenameClick,
  handleListDuplicateClick,
  handleListPinClick,
  handleListDeleteClick,
  newListEmoji,
  newListColor,
  handleNewListEmojiSelect,
  newListName,
  handleNewListNameChange,
  handleNewListKeyDown,
  handleAddList,
  isTouchCapable,
  setIsDraggingList,
}) => (
  <SidebarListSectionBody
    handleSelectAllLists={handleSelectAllLists}
    selectedListId={selectedListId}
    todosLength={todosLength}
    filteredLists={filteredLists}
    todos={todos}
    renamingListId={renamingListId}
    listEmojiHandlers={listEmojiHandlers}
    noopListUpdate={noopListUpdate}
    renameValue={renameValue}
    handleRenameValueChange={handleRenameValueChange}
    handleRenameKeyDown={handleRenameKeyDown}
    handleRenameBlur={handleRenameBlur}
    handleRenameSaveClick={handleRenameSaveClick}
    handleListItemClick={handleListItemClick}
    handleListItemKeyDown={handleListItemKeyDown}
    selectedListIds={selectedListIds}
    handleListRenameClick={handleListRenameClick}
    handleListDuplicateClick={handleListDuplicateClick}
    handleListPinClick={handleListPinClick}
    handleListDeleteClick={handleListDeleteClick}
    newListEmoji={newListEmoji}
    newListColor={newListColor}
    handleNewListEmojiSelect={handleNewListEmojiSelect}
    newListName={newListName}
    handleNewListNameChange={handleNewListNameChange}
    handleNewListKeyDown={handleNewListKeyDown}
    handleAddList={handleAddList}
    isTouchCapable={isTouchCapable}
    setIsDraggingList={setIsDraggingList}
  />
)
const SidebarListSectionBody = ({
  handleSelectAllLists,
  selectedListId,
  todosLength,
  filteredLists,
  todos,
  renamingListId,
  listEmojiHandlers,
  noopListUpdate,
  renameValue,
  handleRenameValueChange,
  handleRenameKeyDown,
  handleRenameBlur,
  handleRenameSaveClick,
  handleListItemClick,
  handleListItemKeyDown,
  selectedListIds,
  handleListRenameClick,
  handleListDuplicateClick,
  handleListPinClick,
  handleListDeleteClick,
  newListEmoji,
  newListColor,
  handleNewListEmojiSelect,
  newListName,
  handleNewListNameChange,
  handleNewListKeyDown,
  handleAddList,
  isTouchCapable,
  setIsDraggingList,
}) => {
  const [draggedListId, setDraggedListId] = useState(null)
  const [dropTargetListId, setDropTargetListId] = useState(null)
  const listCounts = useMemo(() => {
    const counts = new Map()
    todos.forEach((todo) => {
      counts.set(todo.listId, (counts.get(todo.listId) || 0) + 1)
    })
    return counts
  }, [todos])
  const scrollRef = useRef(null)
  const listItemData = useMemo(
    () => ({
      renamingListId,
      listEmojiHandlers,
      noopListUpdate,
      renameValue,
      handleRenameValueChange,
      handleRenameKeyDown,
      handleRenameBlur,
      handleRenameSaveClick,
      handleListItemClick,
      handleListItemKeyDown,
      selectedListIds,
      selectedListId,
      listCounts,
      isTouchCapable,
      handleListRenameClick,
      handleListDuplicateClick,
      handleListPinClick,
      handleListDeleteClick,
      draggedListId,
      dropTargetListId,
      setDraggedListId,
      setDropTargetListId,
      setIsDraggingList,
    }),
    [
      draggedListId,
      handleListDeleteClick,
      handleListDuplicateClick,
      handleListItemClick,
      handleListItemKeyDown,
      handleListPinClick,
      handleListRenameClick,
      handleRenameBlur,
      handleRenameKeyDown,
      handleRenameSaveClick,
      handleRenameValueChange,
      listCounts,
      listEmojiHandlers,
      noopListUpdate,
      dropTargetListId,
      renamingListId,
      renameValue,
      selectedListId,
      selectedListIds,
      setDraggedListId,
      setDropTargetListId,
      setIsDraggingList,
      isTouchCapable,
    ],
  )
  return (
    <div
      id="sidebar-list-section"
      ref={scrollRef}
      className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5"
    >
      <button
        onClick={handleSelectAllLists}
        className={cn(
          'group relative',
          SIDEBAR_LIST_ROW_SHELL,
          selectedListId === 'all'
            ? 'text-primary-foreground'
            : 'text-muted-foreground hover:bg-muted/75 hover:text-foreground',
        )}
      >
        {selectedListId === 'all' && (
          <motion.div
            layoutId="active-list-indicator"
            className="absolute inset-0 rounded-xl bg-primary"
            initial={false}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          />
        )}
        <div className={cn('relative z-10 w-full', SIDEBAR_LIST_ROW_INNER)}>
          <div
            className={cn(
              SIDEBAR_LIST_ICON_SLOT,
              selectedListId === 'all'
                ? 'border-primary-foreground/20 bg-primary-foreground/12'
                : 'bg-background/70',
            )}
          >
            <LayoutPanelLeft className="h-3.5 w-3.5 shrink-0" />
          </div>
          <span className="min-w-0 flex-1 truncate text-left">All Lists</span>
          <span
            className={cn(
              'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold',
              selectedListId === 'all'
                ? 'bg-primary-foreground/12 text-primary-foreground'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {todosLength}
          </span>
        </div>
      </button>

      <SidebarNewListInput
        newListEmoji={newListEmoji}
        newListColor={newListColor}
        handleNewListEmojiSelect={handleNewListEmojiSelect}
        newListName={newListName}
        handleNewListNameChange={handleNewListNameChange}
        handleNewListKeyDown={handleNewListKeyDown}
        handleAddList={handleAddList}
      />

      <VirtualList
        items={filteredLists}
        itemKey="id"
        itemRenderer={SidebarListVirtualItem}
        itemData={listItemData}
        estimatedItemHeight={36}
        itemGap={4}
        minItems={120}
        scrollElement={scrollRef}
        className="relative"
        contentClassName="relative"
      />
    </div>
  )
}
const SidebarListVirtualItem = ({ item: list, ...props }) => {
  return (
    <SidebarListItem
      list={list}
      renamingListId={props.renamingListId}
      listEmojiHandlers={props.listEmojiHandlers}
      noopListUpdate={props.noopListUpdate}
      renameValue={props.renameValue}
      handleRenameValueChange={props.handleRenameValueChange}
      handleRenameKeyDown={props.handleRenameKeyDown}
      handleRenameBlur={props.handleRenameBlur}
      handleRenameSaveClick={props.handleRenameSaveClick}
      handleListItemClick={props.handleListItemClick}
      handleListItemKeyDown={props.handleListItemKeyDown}
      selectedListIds={props.selectedListIds}
      selectedListId={props.selectedListId}
      listCounts={props.listCounts}
      isTouchCapable={props.isTouchCapable}
      handleListRenameClick={props.handleListRenameClick}
      handleListDuplicateClick={props.handleListDuplicateClick}
      handleListPinClick={props.handleListPinClick}
      handleListDeleteClick={props.handleListDeleteClick}
      draggedListId={props.draggedListId}
      dropTargetListId={props.dropTargetListId}
      setDraggedListId={props.setDraggedListId}
      setDropTargetListId={props.setDropTargetListId}
      setIsDraggingList={props.setIsDraggingList}
    />
  )
}
const SidebarFooterSection = ({
  weatherUnit,
  timeFormat,
  handleToggleWeatherUnit,
  handleToggleTimeFormat,
  snap,
  todos,
}) => {
  const [authUser, setAuthUser] = useState(() => loadStoredAuthUser())

  useEffect(() => {
    const syncAuthUser = () => {
      setAuthUser(loadStoredAuthUser())
    }

    window.addEventListener('storage', syncAuthUser)
    window.addEventListener('focus', syncAuthUser)

    return () => {
      window.removeEventListener('storage', syncAuthUser)
      window.removeEventListener('focus', syncAuthUser)
    }
  }, [])

  const handleOpenSettings = useCallback(() => {
    actions.setUIVisibility('showTheme', true)
  }, [])

  return (
    <div id="user-data-navbar" className="flex flex-col border-t border-border/10 bg-muted/20">
      <div className="p-2.5 pb-1">
        <div className="flex items-center gap-3 px-2 py-1.5">
          <SidebarUserAvatar authUser={authUser} />
          <SidebarUserSummary authUser={authUser} />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="ml-auto h-8 w-8 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
                onClick={handleOpenSettings}
                aria-label="Open settings"
              >
                <Settings2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Open settings</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      <div className="px-4 pb-3 pt-0 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <a
            href="/terms"
            className="text-[10px] font-medium text-muted-foreground/50 hover:text-primary transition-colors uppercase tracking-widest"
          >
            Terms
          </a>
          <a
            href="/privacy"
            className="text-[10px] font-medium text-muted-foreground/50 hover:text-primary transition-colors uppercase tracking-widest"
          >
            Privacy
          </a>
        </div>
        <span className="text-[10px] font-medium text-muted-foreground/30 uppercase tracking-widest">
          v1.0.4
        </span>
      </div>
    </div>
  )
}

const loadStoredAuthUser = () => {
  try {
    const storedUser = localStorage.getItem('zoy_auth_user')
    return storedUser ? JSON.parse(storedUser) : null
  } catch (error) {
    console.error('Failed to parse stored auth user', error)
    return null
  }
}

const getInitials = (name, email) => {
  const source = typeof name === 'string' && name.trim() ? name : email || ''
  const parts = source.trim().split(/\s+/).filter(Boolean)

  if (parts.length === 0) {
    return '?'
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }

  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase()
}

const SidebarUserAvatar = ({ authUser }) => {
  const [imageError, setImageError] = useState(false)
  const displayName = authUser?.name || authUser?.email || 'User'
  const avatarUrl = authUser?.picture || authUser?.avatar || authUser?.image

  if (avatarUrl && !imageError) {
    return (
      <img
        src={avatarUrl}
        alt={displayName}
        onError={() => setImageError(true)}
        className="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-border/20"
      />
    )
  }

  return (
    <div className="h-8 w-8 shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
      {getInitials(authUser?.name, authUser?.email)}
    </div>
  )
}

const SidebarUserSummary = ({ authUser }) => {
  const displayName = authUser?.name || 'Signed in'
  const displayEmail = authUser?.email || 'No email available'

  return (
    <div className="flex min-w-0 flex-col">
      <span className="text-xs font-bold truncate">{displayName}</span>
      <span className="text-[10px] text-muted-foreground truncate italic">{displayEmail}</span>
    </div>
  )
}
const SidebarResizeHandle = ({
  isMobile,
  showSidebar,
  handleSidebarResizeMouseDown,
  handleSidebarResizeKeyDown,
}) => (
  <>
    {!isMobile && showSidebar && (
      <button
        type="button"
        className="absolute -right-2 top-0 bottom-0 w-4 cursor-col-resize hover:bg-primary/5 transition-colors z-[75] group/resize resize-handle touch-none"
        onMouseDown={handleSidebarResizeMouseDown}
        onTouchStart={handleSidebarResizeMouseDown}
        onKeyDown={handleSidebarResizeKeyDown}
        aria-label="Resize sidebar"
      />
    )}
  </>
)
const SidebarSelectoLayer = ({
  handleListDragCondition,
  handleListSelectoStart,
  handleListSelecto,
  handleListSelectoEnd,
}) => {
  const onStartRef = useRef(handleListSelectoStart)
  const onMoveRef = useRef(handleListSelecto)
  const onStopRef = useRef(handleListSelectoEnd)
  const onBeforeStartRef = useRef(handleListDragCondition)

  useEffect(() => {
    onStartRef.current = handleListSelectoStart
    onMoveRef.current = handleListSelecto
    onStopRef.current = handleListSelectoEnd
    onBeforeStartRef.current = handleListDragCondition
  }, [handleListSelectoStart, handleListSelecto, handleListSelectoEnd, handleListDragCondition])

  const handleSelectoBeforeStart = useCallback((e) => {
    onBeforeStartRef.current?.({ inputEvent: e.event })
  }, [])
  const handleSelectoStart = useCallback((e) => {
    onStartRef.current?.({ inputEvent: e.event })
  }, [])
  const handleSelectoMove = useCallback((e) => {
    onMoveRef.current?.({
      ...e,
      selected: e.store.selected,
      added: e.store.changed.added,
      removed: e.store.changed.removed,
      inputEvent: e.event,
    })
  }, [])
  const handleSelectoStop = useCallback((e) => {
    onStopRef.current?.({ inputEvent: e.event })
  }, [])

  return (
    <SelectionArea
      boundaries="#sidebar-container"
      selectables=".sidebar-list-item"
      selectionAreaClass="selection-area"
      behaviour={{
        overlap: 'keep',
        intersect: 'touch',
        startThreshold: 10,
        scrolling: {
          speedDivider: 10,
          manualSpeed: 750,
          startScrollMargins: { x: 0, y: 0 },
        },
      }}
      features={{
        touch: true,
        range: true,
        singleTap: { allow: false },
      }}
      onBeforeStart={handleSelectoBeforeStart}
      onStart={handleSelectoStart}
      onMove={handleSelectoMove}
      onStop={handleSelectoStop}
    />
  )
}
const SidebarListItem = ({
  list,
  renamingListId,
  listEmojiHandlers,
  noopListUpdate,
  renameValue,
  handleRenameValueChange,
  handleRenameKeyDown,
  handleRenameBlur,
  handleRenameSaveClick,
  handleListItemClick,
  handleListItemKeyDown,
  selectedListIds,
  selectedListId,
  listCounts,
  isTouchCapable,
  handleListRenameClick,
  handleListDuplicateClick,
  handleListPinClick,
  handleListDeleteClick,
  draggedListId,
  dropTargetListId,
  setDraggedListId,
  setDropTargetListId,
  setIsDraggingList,
}) => {
  return (
    <div className="group relative">
      {renamingListId === list.id ? (
        <SidebarListRenameRow
          list={list}
          listEmojiHandlers={listEmojiHandlers}
          noopListUpdate={noopListUpdate}
          renameValue={renameValue}
          handleRenameValueChange={handleRenameValueChange}
          handleRenameKeyDown={handleRenameKeyDown}
          handleRenameBlur={handleRenameBlur}
          handleRenameSaveClick={handleRenameSaveClick}
        />
      ) : (
        <SidebarListItemMenus
          list={list}
          listEmojiHandlers={listEmojiHandlers}
          noopListUpdate={noopListUpdate}
          selectedListIds={selectedListIds}
          selectedListId={selectedListId}
          listCounts={listCounts}
          isTouchCapable={isTouchCapable}
          handleListItemClick={handleListItemClick}
          handleListItemKeyDown={handleListItemKeyDown}
          handleListRenameClick={handleListRenameClick}
          handleListDuplicateClick={handleListDuplicateClick}
          handleListPinClick={handleListPinClick}
          handleListDeleteClick={handleListDeleteClick}
          draggedListId={draggedListId}
          dropTargetListId={dropTargetListId}
          setDraggedListId={setDraggedListId}
          setDropTargetListId={setDropTargetListId}
          setIsDraggingList={setIsDraggingList}
        />
      )}
    </div>
  )
}
const SidebarListRenameRow = ({
  list,
  listEmojiHandlers,
  noopListUpdate,
  renameValue,
  handleRenameValueChange,
  handleRenameKeyDown,
  handleRenameBlur,
  handleRenameSaveClick,
}) => (
  <div className={cn(SIDEBAR_LIST_ROW_SHELL, SIDEBAR_LIST_ROW_INNER, 'bg-muted/45')}>
    <div className={SIDEBAR_LIST_ICON_SLOT}>
      <ListEmojiPicker
        emoji={list.emoji}
        color={list.color}
        onSelect={listEmojiHandlers.get(list.id) ?? noopListUpdate}
        size="sm"
      />
    </div>
    <div className="relative min-w-0 flex-1">
      <input
        type="text"
        value={renameValue}
        data-list-id={list.id}
        data-list-name={list.name}
        onChange={handleRenameValueChange}
        onKeyDown={handleRenameKeyDown}
        onBlur={handleRenameBlur}
        className="w-full rounded-xl border border-border/10 bg-background px-3 pr-16 py-2 text-[13px] font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
      />
      <RenameSaveAction
        list={list}
        renameValue={renameValue}
        handleRenameSaveClick={handleRenameSaveClick}
      />
    </div>
  </div>
)
const RenameSaveAction = ({ list, renameValue, handleRenameSaveClick }) => (
  <AnimatePresence>
    {renameValue.trim() && renameValue.trim() !== list.name && (
      <motion.div
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 1,
        }}
        exit={{
          opacity: 0,
        }}
        className="absolute right-1 top-1/2 -translate-y-1/2"
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              data-list-id={list.id}
              onClick={handleRenameSaveClick}
              className="h-7 px-2 text-xs font-bold uppercase tracking-wider text-primary hover:bg-primary/10"
            >
              Save
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Save rename</p>
          </TooltipContent>
        </Tooltip>
      </motion.div>
    )}
  </AnimatePresence>
)
const SidebarListItemMenus = ({
  list,
  listEmojiHandlers,
  noopListUpdate,
  selectedListIds,
  selectedListId,
  listCounts,
  isTouchCapable,
  handleListItemClick,
  handleListItemKeyDown,
  handleListRenameClick,
  handleListDuplicateClick,
  handleListPinClick,
  handleListDeleteClick,
  draggedListId,
  dropTargetListId,
  setDraggedListId,
  setDropTargetListId,
  setIsDraggingList,
}) => (
  <div className="contents">
    <SidebarListContextMenu
      list={list}
      listEmojiHandlers={listEmojiHandlers}
      noopListUpdate={noopListUpdate}
      selectedListIds={selectedListIds}
      selectedListId={selectedListId}
      listCounts={listCounts}
      isTouchCapable={isTouchCapable}
      handleListItemClick={handleListItemClick}
      handleListItemKeyDown={handleListItemKeyDown}
      handleListRenameClick={handleListRenameClick}
      handleListDuplicateClick={handleListDuplicateClick}
      handleListPinClick={handleListPinClick}
      handleListDeleteClick={handleListDeleteClick}
      draggedListId={draggedListId}
      dropTargetListId={dropTargetListId}
      setDraggedListId={setDraggedListId}
      setDropTargetListId={setDropTargetListId}
      setIsDraggingList={setIsDraggingList}
    />
    <SidebarListDropdownMenu
      list={list}
      handleListRenameClick={handleListRenameClick}
      handleListDuplicateClick={handleListDuplicateClick}
      handleListPinClick={handleListPinClick}
      handleListDeleteClick={handleListDeleteClick}
    />
  </div>
)
const SidebarListContextMenu = ({
  list,
  listEmojiHandlers,
  noopListUpdate,
  selectedListIds,
  selectedListId,
  listCounts,
  isTouchCapable,
  handleListItemClick,
  handleListItemKeyDown,
  handleListRenameClick,
  handleListDuplicateClick,
  handleListPinClick,
  handleListDeleteClick,
  draggedListId,
  dropTargetListId,
  setDraggedListId,
  setDropTargetListId,
  setIsDraggingList,
}) => {
  let listItemClass = 'text-muted-foreground hover:text-foreground hover:bg-muted'
  if (selectedListIds.has(list.id)) {
    listItemClass = 'bg-primary/20 text-primary'
  } else if (selectedListId === list.id) {
    listItemClass = 'text-secondary-foreground'
  }
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <SidebarListItemButton
          list={list}
          selectedListId={selectedListId}
          listItemClass={listItemClass}
          listCount={listCounts.get(list.id) || 0}
          selectedListIds={selectedListIds}
          isTouchCapable={isTouchCapable}
          onClick={handleListItemClick}
          onKeyDown={handleListItemKeyDown}
          onEmojiSelect={listEmojiHandlers.get(list.id) ?? noopListUpdate}
          onPinClick={handleListPinClick}
          draggedListId={draggedListId}
          dropTargetListId={dropTargetListId}
          setDraggedListId={setDraggedListId}
          setDropTargetListId={setDropTargetListId}
          setIsDraggingList={setIsDraggingList}
        />
      </ContextMenuTrigger>
      <SidebarListContextMenuContent
        list={list}
        selectedListIds={selectedListIds}
        handleListRenameClick={handleListRenameClick}
        handleListDuplicateClick={handleListDuplicateClick}
        handleListPinClick={handleListPinClick}
        handleListDeleteClick={handleListDeleteClick}
      />
    </ContextMenu>
  )
}
const iconHoverProps = {
  whileHover: { scale: 1.15 },
  whileTap: { scale: 0.95 },
  transition: { type: 'spring', stiffness: 400, damping: 17 },
}

const SidebarListItemButton = ({
  list,
  selectedListId,
  listItemClass,
  listCount,
  selectedListIds,
  isTouchCapable,
  onClick,
  onKeyDown,
  onEmojiSelect,
  onPinClick,
  draggedListId,
  dropTargetListId,
  setDraggedListId,
  setDropTargetListId,
  setIsDraggingList,
  onContextMenu,
  ...props
}) => {
  const isSelectedForMulti = selectedListIds?.has(list.id) ?? false
  const handleContextMenu = useCallback(
    (event) => {
      event.stopPropagation()
      onContextMenu?.(event)
    },
    [onContextMenu],
  )
  const handleCheckboxClick = useCallback(
    (event) => {
      event.preventDefault()
      event.stopPropagation()
      onClick?.(event)
    },
    [onClick],
  )
  const handleCheckboxPointerDown = useCallback((event) => {
    event.stopPropagation()
  }, [])
  const handleDragStart = useCallback(
    (event) => {
      event.stopPropagation()
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', list.id)
      setIsDraggingList(true)
      setDraggedListId(list.id)
      setDropTargetListId(list.id)
    },
    [list.id, setDraggedListId, setDropTargetListId, setIsDraggingList],
  )
  const handleDragEnd = useCallback(() => {
    setIsDraggingList(false)
    setDraggedListId(null)
    setDropTargetListId(null)
  }, [setDraggedListId, setDropTargetListId, setIsDraggingList])
  const handleDragOver = useCallback(
    (event) => {
      if (!draggedListId || draggedListId === list.id) {
        return
      }
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      setDropTargetListId(list.id)
    },
    [draggedListId, list.id, setDropTargetListId],
  )
  const handleDrop = useCallback(
    (event) => {
      event.preventDefault()
      event.stopPropagation()
      const activeId = event.dataTransfer.getData('text/plain') || draggedListId
      if (!activeId || activeId === list.id) {
        handleDragEnd()
        return
      }
      actions.reorderList(activeId, list.id)
      handleDragEnd()
    },
    [draggedListId, handleDragEnd, list.id],
  )
  const isDragging = draggedListId === list.id
  const isDropTarget = dropTargetListId === list.id && draggedListId && draggedListId !== list.id
  return (
    <motion.div
      layout
      transition={{ type: 'spring', stiffness: 380, damping: 34 }}
      {...props}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onContextMenu={handleContextMenu}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      data-list-id={list.id}
      role="button"
      tabIndex={0}
      className={cn(
        'sidebar-list-item group/sidebar-list-item relative cursor-pointer select-none active:scale-[0.99]',
        SIDEBAR_LIST_ROW_SHELL,
        isDragging && 'opacity-60',
        isDropTarget && 'ring-2 ring-primary/25',
        listItemClass,
      )}
    >
      {selectedListId === list.id && (
        <motion.div
          layoutId="active-list-indicator"
          className="absolute inset-0 rounded-xl bg-secondary"
          initial={false}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        />
      )}
      <div className={cn('relative z-10 w-full overflow-hidden', SIDEBAR_LIST_ROW_INNER)}>
        <button
          type="button"
          draggable
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          aria-label={`Drag to reorder ${list.name}`}
          data-list-drag-handle="true"
          className="flex h-6 w-6 shrink-0 cursor-grab items-center justify-center rounded-md p-0 text-muted-foreground/35 transition-colors hover:bg-muted hover:text-foreground active:cursor-grabbing"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <div className={SIDEBAR_LIST_ICON_SLOT}>
          <ListEmojiPicker
            emoji={list.emoji}
            color={list.color}
            onSelect={onEmojiSelect}
            size="sm"
          />
        </div>
        <span className="min-w-0 flex-1 truncate text-left text-[13px] font-medium">
          {list.name}
        </span>
        {isTouchCapable && (
          <Checkbox
            data-list-id={list.id}
            data-multi-select="true"
            checked={isSelectedForMulti}
            onClick={handleCheckboxClick}
            onPointerDown={handleCheckboxPointerDown}
            className={cn(
              'mr-1 h-4 w-4 border-muted-foreground/20 bg-background/80 shadow-sm',
              !isSelectedForMulti && 'opacity-40',
            )}
          />
        )}
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            data-list-id={list.id}
            onClick={onPinClick}
            onPointerDown={(e) => e.preventDefault()}
            className={cn(
              'h-5 w-5 rounded-md p-0 text-muted-foreground/55 transition-opacity hover:bg-muted hover:text-foreground',
              list.pinned
                ? 'opacity-100 text-foreground'
                : 'opacity-0 group-hover/sidebar-list-item:opacity-100',
              isTouchCapable && !list.pinned && 'hidden',
            )}
          >
            <motion.div {...iconHoverProps} className="flex items-center justify-center">
              <Pin className={cn('h-3 w-3', list.pinned && 'fill-current')} />
            </motion.div>
          </Button>
          <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
            {listCount}
          </span>
        </div>
      </div>
    </motion.div>
  )
}
const SidebarListContextMenuContent = ({
  list,
  selectedListIds,
  handleListRenameClick,
  handleListDuplicateClick,
  handleListPinClick,
  handleListDeleteClick,
}) => {
  const isMultiSelection = selectedListIds?.has(list.id) && selectedListIds?.size > 1
  const count = isMultiSelection ? selectedListIds.size : 1

  return (
    <ContextMenuContent className="w-44">
      <ContextMenuLabel>Actions {isMultiSelection && `(${count})`}</ContextMenuLabel>
      {!isMultiSelection && (
        <ContextMenuItem
          data-list-id={list.id}
          data-list-name={list.name}
          onClick={handleListRenameClick}
        >
          <span className="flex items-center gap-2">
            <ListTodo className="h-3.5 w-3.5" /> Rename
          </span>
        </ContextMenuItem>
      )}
      <ContextMenuItem data-list-id={list.id} onClick={handleListDuplicateClick}>
        <span className="flex items-center gap-2">
          <Copy className="h-3.5 w-3.5" /> Duplicate
        </span>
      </ContextMenuItem>
      <ContextMenuItem data-list-id={list.id} onClick={handleListPinClick}>
        <span className="flex items-center gap-2">
          <Pin className={cn('h-3.5 w-3.5', list.pinned && 'fill-current')} />
          {list.pinned ? 'Unpin' : 'Pin'}
        </span>
      </ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuLabel>Danger</ContextMenuLabel>
      <ContextMenuItem
        className="text-destructive focus:text-destructive"
        data-list-id={list.id}
        data-list-name={list.name}
        onClick={handleListDeleteClick}
      >
        <span className="flex items-center gap-2">
          <Trash2 className="h-3.5 w-3.5" /> Delete
        </span>
      </ContextMenuItem>
    </ContextMenuContent>
  )
}
const SidebarListDropdownMenu = ({
  list,
  handleListRenameClick,
  handleListDuplicateClick,
  handleListPinClick,
  handleListDeleteClick,
}) => (
  <DropdownMenu>
    <Tooltip>
      <TooltipTrigger asChild>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <MoreVertical className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
      </TooltipTrigger>
      <TooltipContent>
        <p>List options</p>
      </TooltipContent>
    </Tooltip>
    <DropdownMenuContent align="end">
      <DropdownMenuItem
        data-list-id={list.id}
        data-list-name={list.name}
        onClick={handleListRenameClick}
      >
        <ListTodo className="mr-2 h-3.5 w-3.5" />
        <span>Rename</span>
      </DropdownMenuItem>
      <DropdownMenuItem data-list-id={list.id} onClick={handleListDuplicateClick}>
        <Copy className="mr-2 h-3.5 w-3.5" />
        <span>Duplicate</span>
      </DropdownMenuItem>
      <DropdownMenuItem data-list-id={list.id} onClick={handleListPinClick}>
        <Pin className={cn('mr-2 h-3.5 w-3.5', list.pinned && 'fill-current')} />
        <span>{list.pinned ? 'Unpin' : 'Pin'}</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        data-list-id={list.id}
        data-list-name={list.name}
        onClick={handleListDeleteClick}
        className="text-destructive focus:text-destructive"
      >
        <Trash2 className="mr-2 h-3.5 w-3.5" />
        <span>Delete</span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
)
const SidebarNewListInput = ({
  newListEmoji,
  newListColor,
  handleNewListEmojiSelect,
  newListName,
  handleNewListNameChange,
  handleNewListKeyDown,
  handleAddList,
}) => {
  return (
    <div className="mb-1">
      <SidebarNewListRow
        newListEmoji={newListEmoji}
        newListColor={newListColor}
        handleNewListEmojiSelect={handleNewListEmojiSelect}
        newListName={newListName}
        handleNewListNameChange={handleNewListNameChange}
        handleNewListKeyDown={handleNewListKeyDown}
        handleAddList={handleAddList}
      />
    </div>
  )
}
const SidebarNewListRow = ({
  newListEmoji,
  newListColor,
  handleNewListEmojiSelect,
  newListName,
  handleNewListNameChange,
  handleNewListKeyDown,
  handleAddList,
}) => (
  <div
    className={cn(
      'group/new-list-row border border-transparent bg-muted/35 hover:bg-muted/55 focus-within:border-primary/20 focus-within:bg-background/90 focus-within:ring-4 focus-within:ring-primary/8',
      SIDEBAR_LIST_ROW_SHELL,
      SIDEBAR_LIST_ROW_INNER,
    )}
  >
    <div
      className={cn(
        SIDEBAR_LIST_ICON_SLOT,
        'group-focus-within/new-list-row:border-primary/25 group-focus-within/new-list-row:bg-primary/5',
      )}
    >
      <ListEmojiPicker
        emoji={newListEmoji}
        color={newListColor}
        onSelect={handleNewListEmojiSelect}
        size="sm"
        defaultIcon={
          <Smile className="h-4 w-4 text-muted-foreground/40 group-focus-within/new-list-row:text-primary transition-colors" />
        }
      />
    </div>
    <input
      type="text"
      value={newListName}
      onChange={handleNewListNameChange}
      onKeyDown={handleNewListKeyDown}
      placeholder="Add list"
      className="flex-1 bg-transparent border-none p-0 text-[13px] font-medium focus:outline-none placeholder:text-muted-foreground/40"
    />
    <NewListAddButton newListName={newListName} handleAddList={handleAddList} />
  </div>
)

const NewListAddButton = ({ newListName, handleAddList }) => (
  <AnimatePresence>
    {newListName.trim() && (
      <motion.div
        initial={{ opacity: 0, scale: 0.8, x: 8 }}
        animate={{ opacity: 1, scale: 1, x: 0 }}
        exit={{ opacity: 0, scale: 0.8, x: 8 }}
        transition={{ type: 'spring', stiffness: 500, damping: 25 }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              onClick={handleAddList}
              className="h-7 w-7 p-0 rounded-md shadow-sm bg-primary hover:bg-primary/90 text-primary-foreground transition-all active:scale-90"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Add list</p>
          </TooltipContent>
        </Tooltip>
      </motion.div>
    )}
  </AnimatePresence>
)
